import { useCallback, useEffect, useMemo, useState } from "react";
import { AdminShell } from "@project/admin/components/admin-shell";
import { TablePagination } from "@project/admin/components/table-pagination";
import { AddDonationDialog } from "@project/admin/components/donations/add-donation-dialog";
import { DonationDetailDialog } from "@project/admin/components/donations/donation-detail-dialog";
import { DonationListFilters } from "@project/admin/components/donations/donation-list-filters";
import {
  buildDonationListApiParams,
  donationListFilterInitialState,
  type DonationFilterOption,
  type DonationListFilterState,
} from "@project/admin/components/donations/donation-list-filter-utils";
import { Button } from "@project/components/ui/button";
import { Badge } from "@project/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@project/components/ui/table";
import { Download, Eye, Plus } from "lucide-react";
import { projectToast } from "@project/lib/toast";
import { generateReceiptPdf } from "@project/lib/receipt";
import { exportApiRowsToCsv } from "@project/lib/csv";
import { CsvExportButton } from "@project/admin/components/csv-export-button";
import { donationDetailService } from "@project/services/admin/donation-detail.service";
import { donationMasterService } from "@project/services/admin/donation-master.service";
import { donationSchemeService } from "@project/services/admin/donation-scheme.service";
import { fetchAllListData } from "@project/services/admin/list-api-helpers";
import { PAYMENT_MODE_OPTIONS } from "@project/types/donation-donor";
import type { ApiPagination } from "@project/types/location";
import {
  buildDonationAddress,
  formatDonationDate,
  formatPaymentStatusLabel,
  getDonationAmount,
  getDonationCampaignLabel,
  getDonationDonorName,
  getDonationEmail,
  getDonationModeLabel,
  getDonationPhone,
  getDonationReceiptNo,
  getDonationTypeLabel,
  getPaymentStatus,
  paymentStatusBadgeClass,
  type DonationDetailRecord,
} from "@project/types/donation-detail";

const DEFAULT_PAGE_SIZE = 10;

function TableEllipsisText({
  value,
  className,
}: {
  value: string | null | undefined;
  className?: string;
}) {
  const text = value?.trim() ?? "—";
  return (
    <span
      className={`block min-w-0 truncate ${className ?? ""}`.trim()}
      title={text}
    >
      {text}
    </span>
  );
}

export default function Page() {
  const [rows, setRows] = useState<DonationDetailRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [listLoading, setListLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [pagination, setPagination] = useState<ApiPagination>({
    count: 0,
    current_page: 1,
    total_pages: 1,
    next: null,
    previous: null,
  });

  const [donationFilters, setDonationFilters] =
    useState<DonationListFilterState>(donationListFilterInitialState);
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [debouncedMessage, setDebouncedMessage] = useState("");
  const [filtersExpanded, setFiltersExpanded] = useState(false);

  const [donationTypeOptions, setDonationTypeOptions] = useState<
    DonationFilterOption[]
  >([]);
  const [paymentModeOptions, setPaymentModeOptions] = useState<
    DonationFilterOption[]
  >(
    PAYMENT_MODE_OPTIONS.map(mode => ({
      value: mode.value,
      label: mode.label,
    }))
  );
  const [schemeOptions, setSchemeOptions] = useState<DonationFilterOption[]>(
    []
  );

  const [isAddDonationOpen, setIsAddDonationOpen] = useState(false);
  const [selectedDonation, setSelectedDonation] =
    useState<DonationDetailRecord | null>(null);
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [exportingCsv, setExportingCsv] = useState(false);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedSearch(donationFilters.search);
    }, 300);
    return () => window.clearTimeout(timer);
  }, [donationFilters.search]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedMessage(donationFilters.message);
    }, 300);
    return () => window.clearTimeout(timer);
  }, [donationFilters.message]);

  useEffect(() => {
    setPage(1);
  }, [
    debouncedSearch,
    debouncedMessage,
    donationFilters.donation_type,
    donationFilters.status,
    donationFilters.payment_mode,
    donationFilters.scheme,
  ]);

  useEffect(() => {
    let ignore = false;
    const loadMeta = async () => {
      try {
        const [typesResult, schemesResult] = await Promise.all([
          donationMasterService.donationType.listPage({
            page: 1,
            page_size: 100,
            status: "active",
          }),
          donationSchemeService.listDonationSchemesPage({
            page: 1,
            page_size: 200,
          }),
        ]);
        if (ignore) {
          return;
        }
        setDonationTypeOptions(
          typesResult.data
            .map(type => ({
              value: type.name?.trim() ?? String(type.id),
              label: type.name?.trim() ?? String(type.id),
            }))
            .filter(option => option.value)
            .sort((a, b) => a.label.localeCompare(b.label))
        );
        setSchemeOptions(
          schemesResult.data
            .map(scheme => ({
              value: String(scheme.id),
              label: scheme.title?.trim() || `Campaign #${scheme.id}`,
            }))
            .sort((a, b) => a.label.localeCompare(b.label))
        );
      } catch {
        if (!ignore) {
          projectToast.error("Failed to load filter options");
        }
      }
    };
    void loadMeta();
    return () => {
      ignore = true;
    };
  }, []);

  const activeFilters = useMemo(
    () => ({
      ...donationFilters,
      search: debouncedSearch,
      message: debouncedMessage,
    }),
    [donationFilters, debouncedSearch, debouncedMessage]
  );

  const fetchDonations = useCallback(async () => {
    setListLoading(true);
    try {
      const params = buildDonationListApiParams(activeFilters, page, pageSize);
      const result =
        await donationDetailService.listDonationDetailsPage(params);
      setRows(result.data ?? []);
      setPagination(result.pagination);
      if (result.pagination.current_page !== page) {
        setPage(result.pagination.current_page);
      }

      const modes = [
        ...new Set(
          result.data
            .map(row => row.payment_mode?.trim())
            .filter(Boolean) as string[]
        ),
      ].sort((a, b) => a.localeCompare(b));
      if (modes.length > 0) {
        setPaymentModeOptions(
          modes.map(mode => ({ value: mode, label: mode }))
        );
      }
    } catch {
      projectToast.error("Failed to load donations");
      setRows([]);
    } finally {
      setListLoading(false);
      setLoading(false);
    }
  }, [activeFilters, page, pageSize]);

  useEffect(() => {
    void fetchDonations();
  }, [fetchDonations]);

  const downloadReceipt = (row: DonationDetailRecord) => {
    generateReceiptPdf({
      receiptNo: getDonationReceiptNo(row),
      date: String(row.created_at ?? row.donation_date ?? ""),
      donationScheme: row.scheme_title ?? "—",
      donorName: getDonationDonorName(row),
      donorMobile: getDonationPhone(row),
      donorEmail: row.email ?? "",
      donorAddress: buildDonationAddress(row),
      donorPan: row.pan_number ?? "",
      organization: row.organization ?? "",
      amount: getDonationAmount(row),
      donationType: getDonationTypeLabel(row),
      paymentMode: getDonationModeLabel(row),
      cashOrChequeNo:
        row.by_cheque_no ??
        row.transaction_id ??
        row.payments?.[0]?.razorpay_payment_id ??
        "",
      remarks: row.message ?? row.remarks ?? "",
    });
  };

  const handleViewDetails = async (row: DonationDetailRecord) => {
    setSelectedDonation(null);
    setIsDetailsOpen(true);
    setLoadingDetails(true);
    try {
      const details = await donationDetailService.getDonationDetail(row.id);
      setSelectedDonation(details ?? row);
    } catch {
      setSelectedDonation(row);
      projectToast.error("Could not load full donation details");
    } finally {
      setLoadingDetails(false);
    }
  };

  const exportCsv = async () => {
    setExportingCsv(true);
    try {
      const {
        page: _page,
        page_size: _pageSize,
        ...filterQuery
      } = buildDonationListApiParams(activeFilters, 1, pageSize);
      const exportRows = await fetchAllListData(
        query => donationDetailService.listDonationDetailsPage(query),
        filterQuery
      );
      if (!exportRows.length) {
        projectToast.error("Nothing to export");
        return;
      }
      exportApiRowsToCsv(
        `donations-${new Date().toISOString().slice(0, 10)}`,
        exportRows as Record<string, unknown>[]
      );
      projectToast.success(`Exported ${exportRows.length} donations`);
    } catch {
      projectToast.error("Failed to export donations");
    } finally {
      setExportingCsv(false);
    }
  };

  const total = rows.reduce((sum, row) => sum + getDonationAmount(row), 0);
  const tableLoading = loading || listLoading;

  return (
    <AdminShell
      title="Donations"
      subtitle="View and record donation entries with receipts"
    >
      <div className="space-y-4">
        <DonationListFilters
          filters={donationFilters}
          onFiltersChange={setDonationFilters}
          filtersExpanded={filtersExpanded}
          onFiltersExpandedChange={setFiltersExpanded}
          donationTypes={donationTypeOptions}
          paymentModes={paymentModeOptions}
          schemes={schemeOptions}
        />

        <div className="flex flex-wrap items-center gap-2 sm:justify-end">
          <div className="mr-auto text-sm text-muted-foreground sm:mr-0">
            Total:{" "}
            <span className="font-bold text-foreground">
              ₹{total.toLocaleString("en-IN")}
            </span>
          </div>
          <CsvExportButton onExport={exportCsv} exporting={exportingCsv} />
          <Button
            type="button"
            onClick={() => setIsAddDonationOpen(true)}
            className="gap-1 rounded-xl"
          >
            <Plus className="h-4 w-4" /> Add Donation
          </Button>
        </div>

        <div className="-mx-3 overflow-x-auto rounded-2xl border border-border bg-card shadow-sm sm:mx-0">
          <Table className="[&_td]:border-border/70 min-w-[1100px] border-collapse [&_td]:border [&_th]:border [&_th]:border-border">
            <TableHeader className="bg-muted/60">
              <TableRow className="hover:bg-muted/60">
                <TableHead className="px-3 py-2.5 font-semibold">
                  Sr. No
                </TableHead>
                <TableHead className="px-3 py-2.5 font-semibold">
                  Name
                </TableHead>
                <TableHead className="px-3 py-2.5 font-semibold">
                  Email
                </TableHead>
                <TableHead className="px-3 py-2.5 font-semibold">
                  Phone
                </TableHead>
                <TableHead className="px-3 py-2.5 font-semibold">
                  Amount
                </TableHead>
                <TableHead className="px-3 py-2.5 font-semibold">
                  Donation Type
                </TableHead>
                <TableHead className="px-3 py-2.5 font-semibold">
                  Mode
                </TableHead>
                <TableHead className="px-3 py-2.5 font-semibold">
                  Campaign / Purpose
                </TableHead>
                <TableHead className="px-3 py-2.5 font-semibold">
                  Payment Status
                </TableHead>
                <TableHead className="px-3 py-2.5 font-semibold">
                  Date
                </TableHead>
                <TableHead className="px-3 py-2.5 text-right font-semibold">
                  Action
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {tableLoading ? (
                <TableRow>
                  <TableCell
                    colSpan={11}
                    className="py-10 text-center text-muted-foreground"
                  >
                    Loading…
                  </TableCell>
                </TableRow>
              ) : rows.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={11}
                    className="py-10 text-center text-muted-foreground"
                  >
                    No donations found.
                  </TableCell>
                </TableRow>
              ) : (
                rows.map((row, index) => {
                  const status = getPaymentStatus(row);
                  return (
                    <TableRow
                      key={row.id}
                      className={
                        index % 2 === 0 ? "bg-background" : "bg-muted/20"
                      }
                    >
                      <TableCell className="px-3 py-2.5">
                        {(page - 1) * pageSize + index + 1}
                      </TableCell>
                      <TableCell className="max-w-[160px] px-3 py-2.5">
                        <TableEllipsisText
                          value={getDonationDonorName(row)}
                          className="font-medium"
                        />
                      </TableCell>
                      <TableCell className="max-w-[220px] px-3 py-2.5">
                        <TableEllipsisText value={getDonationEmail(row)} />
                      </TableCell>
                      <TableCell className="max-w-[130px] px-3 py-2.5">
                        <TableEllipsisText value={getDonationPhone(row)} />
                      </TableCell>
                      <TableCell className="px-3 py-2.5 font-bold">
                        ₹{getDonationAmount(row).toLocaleString("en-IN")}
                      </TableCell>
                      <TableCell className="max-w-[140px] px-3 py-2.5">
                        <TableEllipsisText value={getDonationTypeLabel(row)} />
                      </TableCell>
                      <TableCell className="max-w-[120px] px-3 py-2.5">
                        <TableEllipsisText
                          value={getDonationModeLabel(row)}
                          className="font-medium capitalize"
                        />
                      </TableCell>
                      <TableCell className="max-w-[200px] px-3 py-2.5">
                        <TableEllipsisText
                          value={getDonationCampaignLabel(row)}
                          className="font-medium"
                        />
                      </TableCell>
                      <TableCell className="px-3 py-2.5">
                        <Badge
                          className={`capitalize ${paymentStatusBadgeClass(status)}`}
                        >
                          {formatPaymentStatusLabel(status)}
                        </Badge>
                      </TableCell>
                      <TableCell className="px-3 py-2.5">
                        {formatDonationDate(
                          row.created_at ?? row.donation_date
                        )}
                      </TableCell>
                      <TableCell className="px-3 py-2.5 text-right">
                        <div className="flex justify-end gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-8 w-8 p-0"
                            title="View Details"
                            onClick={() => void handleViewDetails(row)}
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-8 gap-1.5"
                            onClick={() => downloadReceipt(row)}
                            title="Download Receipt"
                          >
                            <Download className="h-4 w-4" />
                            <span className="text-xs">Receipt</span>
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </div>

        <TablePagination
          page={page}
          pageSize={pageSize}
          totalCount={pagination.count}
          totalPages={pagination.total_pages}
          loading={tableLoading}
          onPageChange={setPage}
          onPageSizeChange={size => {
            setPageSize(size);
            setPage(1);
          }}
        />
      </div>

      <AddDonationDialog
        open={isAddDonationOpen}
        onOpenChange={setIsAddDonationOpen}
        donationTypes={donationTypeOptions}
        schemes={schemeOptions}
        onSuccess={() => void fetchDonations()}
      />

      <DonationDetailDialog
        open={isDetailsOpen}
        onOpenChange={setIsDetailsOpen}
        donation={selectedDonation}
        loading={loadingDetails}
        onDownloadReceipt={downloadReceipt}
      />
    </AdminShell>
  );
}
